/**
 * Assistant context-scope selector (frontend-display.md §4.4).
 *
 * A quiet, borderless dropdown at the panel top that picks the conversation's
 * active context — the future `useAgent.name` (`dept:<id>` / `project:<id>` /
 * `user:<id>`). Departments + projects come from `/api/contexts` filtered to
 * `context_type ∈ {department, project}`; "Just me" is always present.
 *
 * SHELL: selecting a scope only re-keys local state here; the LATER slice
 * re-points `useAgent` at the matching Durable Object instance.
 */
import * as React from "react";
import { useList } from "@refinedev/core";
import type { Context } from "@aos/shared";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Icon } from "@/components/ui/icon";
import type { AssistantScope } from "@/components/assistant/types";
import { SELF_SCOPE } from "@/components/assistant/use-assistant";

interface ContextSwitcherProps {
  scope: AssistantScope;
  onScopeChange: (scope: AssistantScope) => void;
}

/** Encode a scope as a stable Select value (`kind:id`). */
function toValue(scope: { kind: string; id: string }): string {
  return `${scope.kind}:${scope.id}`;
}

export function ContextSwitcher({ scope, onScopeChange }: ContextSwitcherProps) {
  // Degrades gracefully: if /api/contexts is unavailable, only "Just me" shows.
  const { data, isError } = useList<Context>({
    resource: "contexts",
    pagination: { pageSize: 200 },
    sorters: [{ field: "priority", order: "desc" }],
    queryOptions: { retry: false },
  });

  // Derive the list once and memoize on the stable `data` reference. `data?.data`
  // is referentially stable across renders while the query result is unchanged,
  // whereas `.filter(...)` produces a fresh array every render — depending on
  // those fresh arrays would make every downstream `useMemo` recompute (and any
  // effect that consumed them re-fire) on every render.
  const contexts = React.useMemo(() => data?.data ?? [], [data]);
  const departments = React.useMemo(
    () => contexts.filter((c) => c.context_type === "department"),
    [contexts],
  );
  const projects = React.useMemo(
    () => contexts.filter((c) => c.context_type === "project"),
    [contexts],
  );

  const scopesById = React.useMemo(() => {
    const map = new Map<string, AssistantScope>();
    map.set(toValue(SELF_SCOPE), SELF_SCOPE);
    for (const c of departments) {
      const s: AssistantScope = {
        kind: "department",
        id: c.id,
        label: c.name,
        contextKey: `dept:${c.id}`,
      };
      map.set(toValue(s), s);
    }
    for (const c of projects) {
      const s: AssistantScope = {
        kind: "project",
        id: c.id,
        label: c.name,
        contextKey: `project:${c.id}`,
      };
      map.set(toValue(s), s);
    }
    return map;
  }, [departments, projects]);

  const onValueChange = (value: string) => {
    const next = scopesById.get(value);
    if (next) onScopeChange(next);
  };

  return (
    <Select value={toValue(scope)} onValueChange={onValueChange}>
      {/* No hard border — a quiet hover affordance only (§4.3). */}
      <SelectTrigger
        aria-label="Assistant context scope"
        data-testid="assistant-context-switcher"
        className="h-8 w-full border-none bg-transparent px-2 text-sm shadow-none hover:bg-accent focus:ring-0 focus-visible:ring-0"
      >
        <span className="shrink-0 text-muted-foreground">
          <Icon name="scope" size={16} aria-hidden />
        </span>
        <SelectValue placeholder="Just me" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={toValue(SELF_SCOPE)}>{SELF_SCOPE.label}</SelectItem>
        </SelectGroup>
        {departments.length > 0 && (
          <SelectGroup>
            <SelectLabel>Departments</SelectLabel>
            {departments.map((c) => (
              <SelectItem key={c.id} value={`department:${c.id}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {projects.length > 0 && (
          <SelectGroup>
            <SelectLabel>Projects</SelectLabel>
            {projects.map((c) => (
              <SelectItem key={c.id} value={`project:${c.id}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {isError && (
          <SelectGroup>
            <SelectLabel>Scopes unavailable — using “Just me”</SelectLabel>
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );
}

export default ContextSwitcher;
